/**
 * The colour a game lends the room it is looked at in.
 *
 * A store where every page is the same charcoal is a store where every game feels like the same
 * game. This is the one value that changes that: one colour, taken from the game's own artwork,
 * drawn behind the page.
 *
 * Two rules keep it from eating the design system, and both matter more than the colour itself:
 *
 * - **It is never a control.** Lemon is the brand and the focus ring, jade is an enabled
 *   setting, and a status is stated in words (field rules 02 and 03). The accent is allowed in
 *   the cold open's scrim, the vignette, the wallpaper wash and the hairline at the top of the
 *   page. Nowhere else, ever. A button, a badge, a border or any state tinted with this is a
 *   bug, not a refinement.
 * - **It is mid, and the sheet darkens it.** What is stored here is a legible mid-tone rather
 *   than the ink-dark wash that ends up on screen, because the four places that draw it each
 *   want a different amount. They `color-mix` toward ink themselves, so the depth of the room
 *   can be retuned in the stylesheet without re-processing anybody's artwork.
 *
 * The extraction is pure and lives here so it can be tested against real numbers. The canvas
 * that produces the pixels is the admin's (`apps/client/src/lib/accent.ts`): it runs once, on
 * the machine that uploaded the picture, and what lands in the database is six characters. No
 * player's device ever samples an image — which is the whole reason the page is already the
 * right colour on first paint rather than a moment after it.
 */

import { z } from 'zod';
import type { GameKind } from './library.js';

/** Lowercase `#rrggbb`. One form, so nothing downstream has to parse two. */
export const ACCENT_PATTERN = /^#[0-9a-f]{6}$/;

export const accentColorSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(ACCENT_PATTERN, 'A colour is #rrggbb.');

/**
 * The colour a game falls back to when it has no artwork to take one from.
 *
 * By rung rather than one shared default, because the rung is the honest thing to say about a
 * game with nothing to show yet: a prototype and a finished game should not open the same room.
 */
export const ACCENT_BY_KIND: Record<GameKind, string> = {
  game: '#5b6f8a',
  early_access: '#6d7a5a',
  dev: '#7a6a55',
  prototype: '#6b6470',
};

/** The colour to draw for a game, whether or not anything was ever extracted for it. */
export function accentOf(game: { accentColor?: string | null; kind: GameKind }): string {
  return game.accentColor ?? ACCENT_BY_KIND[game.kind];
}

/* ------------------------------------------------------------------ oklab */

/**
 * OKLab rather than HSL, because the scoring below weighs pixels by how colourful they are and
 * HSL's saturation is not that: a pastel and a fluorescent can report the same number. OKLCh
 * chroma is perceptual, so "the most colourful part of this picture" means what it says.
 */
interface Oklch {
  l: number;
  c: number;
  /** Radians, so the circular mean below does not have to keep converting. */
  h: number;
}

function toLinear(channel: number): number {
  const v = channel / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

function toGamma(channel: number): number {
  const v = channel <= 0.0031308 ? channel * 12.92 : 1.055 * channel ** (1 / 2.4) - 0.055;
  return Math.min(255, Math.max(0, Math.round(v * 255)));
}

function rgbToOklch(r: number, g: number, b: number): Oklch {
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

  const lightness = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  return { l: lightness, c: Math.hypot(a, bb), h: Math.atan2(bb, a) };
}

function oklchToHex({ l, c, h }: Oklch): string {
  const a = Math.cos(h) * c;
  const b = Math.sin(h) * c;

  const lp = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const mp = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const sp = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;

  const r = toGamma(4.0767416621 * lp - 3.3077115913 * mp + 0.2309699292 * sp);
  const g = toGamma(-1.2684380046 * lp + 2.6097574011 * mp - 0.3413193965 * sp);
  const bl = toGamma(-0.0041960863 * lp - 0.7034186147 * mp + 1.707614701 * sp);

  return `#${[r, g, bl].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/* -------------------------------------------------------------- extraction */

/**
 * How many hue bins the wheel is cut into. Thirty degrees each: wide enough that a gradient sky
 * lands in one bin, narrow enough that a red logo does not share with an orange one.
 */
const HUE_BINS = 12;

/**
 * Below this, the winning bin is not a colour anybody would name — it is noise in a greyscale
 * picture. Expressed as a share of the total chroma in the image so it does not depend on how
 * many pixels were sampled.
 */
const BIN_SHARE_FLOOR = 0.18;

/** The band a stored accent is held in, so no artwork can produce a room that cannot be read. */
const L_MIN = 0.45;
const L_MAX = 0.72;
const C_MIN = 0.04;
const C_MAX = 0.16;

function clampToBand(colour: Oklch): Oklch {
  return {
    l: Math.min(L_MAX, Math.max(L_MIN, colour.l)),
    c: Math.min(C_MAX, Math.max(C_MIN, colour.c)),
    h: colour.h,
  };
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
  return sorted[mid] ?? 0;
}

/**
 * The mean of a set of angles, which is not the mean of their numbers.
 *
 * Two hues either side of the wrap — a warm red at 5 degrees and another at 355 — average to
 * 180, which is cyan, if you add them up and divide. Summing them as vectors gives the red back.
 */
function circularMean(radians: number[], weights: number[]): number {
  let x = 0;
  let y = 0;
  for (let i = 0; i < radians.length; i += 1) {
    const weight = weights[i] ?? 1;
    x += Math.cos(radians[i] ?? 0) * weight;
    y += Math.sin(radians[i] ?? 0) * weight;
  }
  return Math.atan2(y, x);
}

/**
 * The one colour that best stands for a picture, as `#rrggbb`.
 *
 * `rgba` is the raw bytes of a downsampled frame — four per pixel, the shape a canvas hands
 * back. Small is the point: 32 x 32 is a thousand pixels, which is plenty to find what a piece
 * of key art is mostly doing and cheap enough to run on a phone.
 *
 * The averaging trap is worth naming, because the obvious implementation falls straight into
 * it: the mean of a colourful picture is brown. Every hue cancels every other one out and what
 * is left is the lightness. So this bins by hue instead and weighs each bin by the chroma in it
 * rather than by the number of pixels — a grey sky over a red desert is a red picture, because
 * the sky contributes almost nothing to any bin while the desert piles into one.
 *
 * Returns null when there was nothing to read: an empty frame, or one that is entirely black,
 * white or transparent. An honestly greyscale picture is not null — it returns its grey, pulled
 * up to the chroma floor, because "this game's art has no colour in it" is itself a fact about
 * the game and the room should say it.
 */
export function accentFromPixels(rgba: ArrayLike<number>): string | null {
  const opaque: Oklch[] = [];

  for (let i = 0; i + 3 < rgba.length; i += 4) {
    // A transparent pixel has no colour, whatever its channels happen to say. A logo is mostly
    // these, and without the test every wordmark would come back as whatever its matte was.
    if ((rgba[i + 3] ?? 0) < 128) continue;
    opaque.push(rgbToOklch(rgba[i] ?? 0, rgba[i + 1] ?? 0, rgba[i + 2] ?? 0));
  }

  if (opaque.length === 0) return null;

  // Letterboxing, a fade to black, a blown-out sky: present in quantity, and none of it is what
  // the picture is about.
  //
  // But the cut is for outliers inside a picture that has something else in it, not for the
  // picture. Art that is entirely pale, or a night scene that is entirely nearly black, has a
  // colour and it is the one it is made of — dropping every pixel and answering null would hand
  // those two the rung's fallback, which is the opposite of what the extraction is for.
  //
  // So when the cut takes everything, the frame stands, on one condition: there has to be some
  // colour in it. That is what separates pale cream art, which has a hue and should keep it,
  // from a frame that is flat black or flat white, which has none and where any answer would be
  // one this code made up. The clamp at the end is what makes the first case legible.
  const lit = opaque.filter((colour) => colour.l >= 0.08 && colour.l <= 0.95);
  let samples = lit;
  if (lit.length === 0) {
    if (median(opaque.map((s) => s.c)) < 0.01) return null;
    samples = opaque;
  }

  const bins: Oklch[][] = Array.from({ length: HUE_BINS }, () => []);
  let totalChroma = 0;
  for (const sample of samples) {
    // `atan2` is -pi..pi and a bin index wants 0..1.
    const turn = (sample.h / (Math.PI * 2) + 1) % 1;
    const bin = Math.min(HUE_BINS - 1, Math.floor(turn * HUE_BINS));
    bins[bin]?.push(sample);
    totalChroma += sample.c;
  }

  const scored = bins.map((bin) => ({
    bin,
    score: bin.reduce((sum, sample) => sum + sample.c, 0),
  }));
  const best = scored.reduce((a, b) => (b.score > a.score ? b : a));

  // Nothing in this picture is colourful enough to name. Its own median grey, lifted to the
  // floor by the clamp, which is the honest answer rather than a made-up hue.
  const winner =
    totalChroma === 0 || best.score / totalChroma < BIN_SHARE_FLOOR ? samples : best.bin;

  // The median inside the winning bin rather than its mean: one fluorescent pixel in an
  // otherwise muted bin should not decide what the room looks like.
  return oklchToHex(
    clampToBand({
      l: median(winner.map((s) => s.l)),
      c: median(winner.map((s) => s.c)),
      h: circularMean(
        winner.map((s) => s.h),
        winner.map((s) => s.c),
      ),
    }),
  );
}
